import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { MiniAvatar } from '@/frontend/components/MiniAvatar'
import { agentHueStyle } from '@/frontend/lib/agentHue'

// Item da fila "Precisa de você" (SZ-1599/C.4) — shape servido por
// Dashboard::ApprovalQueue (home e perfil do agente usam o mesmo contrato).
export interface ApprovalItem {
  id: string
  title: string
  description: string | null
  worker_id: string
  worker_name: string
  requested_at: string
  approve_path: string
  decline_path: string
  snooze_path: string
  detail_path?: string
  detail_label?: string
}

export interface ApprovalQueueState {
  visibleItems: ApprovalItem[]
  act: (item: ApprovalItem, path: string) => void
  actionFailed: boolean
}

/**
 * Ações otimistas da fila (SZ-1599/C.4): a linha some no clique e o PATCH
 * recarrega apenas `reloadProp` + flash (os headers parciais do Inertia
 * atravessam o redirect-back do servidor). Ao terminar, o estado do servidor
 * manda: sucesso → o item não volta nas props; negado/expirado → o item
 * reaparece e o flash de alerta explica. `onError` (bag de erros) mostra a
 * mensagem discreta local.
 */
export function useApprovalQueue(items: ApprovalItem[], reloadProp: string): ApprovalQueueState {
  const [hiddenIds, setHiddenIds] = useState<string[]>([])
  const [actionFailed, setActionFailed] = useState(false)

  const visibleItems = items.filter((item) => !hiddenIds.includes(item.id))

  const act = (item: ApprovalItem, path: string) => {
    setActionFailed(false)
    setHiddenIds((ids) => [...ids, item.id])
    router.patch(path, {}, {
      preserveScroll: true,
      only: [reloadProp, 'flash'],
      onError: () => setActionFailed(true),
      // Sempre solta o id: com as props novas já aplicadas, ou o item saiu da
      // fila (sucesso) ou reaparece de propósito (falha).
      onFinish: () => setHiddenIds((ids) => ids.filter((id) => id !== item.id)),
    })
  }

  return { visibleItems, act, actionFailed }
}

interface ListProps {
  queue: ApprovalQueueState
  // O card no perfil do agente omite o nome (o contexto já é o agente).
  showWorkerName?: boolean
}

// Ações do protótipo: Aprovar em verde-ghost, Adiar/Recusar em ghost — 11.5px,
// peso 600, radius 7, alinhadas à direita na MESMA linha em desktop.
const ACTION =
  'rounded-[7px] border px-[11px] py-1 text-[11.5px] font-semibold transition-colors duration-150'
const ACTION_GHOST = `${ACTION} border-input text-muted-foreground hover:bg-secondary hover:text-foreground`
const ACTION_APPROVE = `${ACTION} border-success/35 text-success hover:bg-success/12`

export function ApprovalQueueList({ queue, showWorkerName = true }: ListProps) {
  return (
    <div className="space-y-1.5">
      {queue.actionFailed && (
        <p role="alert" className="text-xs text-destructive">
          Não foi possível concluir a ação agora. Tente novamente.
        </p>
      )}
      <ul className="rounded-xl border border-border bg-card px-3.5">
        {queue.visibleItems.map((item) => (
          <li
            key={item.id}
            style={agentHueStyle(item.worker_id)}
            className="flex flex-col gap-2 border-t border-border py-2 text-[13.5px] first:border-t-0 sm:flex-row sm:items-center sm:gap-2.5"
          >
            {showWorkerName && <MiniAvatar name={item.worker_name} workerId={item.worker_id} />}
            <div className="min-w-0 flex-1 text-muted-foreground">
              <span className="block truncate font-medium text-foreground">{item.title}</span>
              {item.description && (
                <p className="truncate text-[11.5px] text-faint-foreground">{item.description}</p>
              )}
            </div>
            <div className="flex flex-none items-center gap-1.5">
              {item.detail_path ? <Link className={ACTION_APPROVE} href={item.detail_path}>{item.detail_label}</Link> : <>
              <button type="button" className={ACTION_APPROVE} onClick={() => queue.act(item, item.approve_path)}>
                Aprovar
              </button>
              <button type="button" className={ACTION_GHOST} onClick={() => queue.act(item, item.snooze_path)}>
                Adiar
              </button>
              <button type="button" className={ACTION_GHOST} onClick={() => queue.act(item, item.decline_path)}>
                Recusar
              </button>
              </>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
